import React, { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'

import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import CarteService from '../service/CarteDashboard'
import { getRegionCoords } from '../utils/hooks/regionName'

// ---------------------------------------------------------------------------
// Fix icônes Leaflet
// ---------------------------------------------------------------------------
delete L.Icon.Default.prototype._getIconUrl

L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

// ---------------------------------------------------------------------------
// Générateurs d'icônes
// ---------------------------------------------------------------------------
const createPlantIcon = (count) =>
    L.divIcon({
        html: `
        <div style="
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            font-weight: 700;
            width: 34px;
            height: 34px;
            background: #ffffff;
            border: 2px solid #2e7d32;
            color: #2e7d32;
            border-radius: 50%;
            box-shadow: 0 4px 6px rgba(0,0,0,0.15);
        ">
            ${count || 0}
        </div>
    `,
        className: 'custom-marker-pb',
        iconSize: [34, 34],
        iconAnchor: [17, 17]
    })

const getDynamicSize = (value) => {
    if (!value) return 24
    return Math.min(44, Math.max(24, Math.log10(value) * 12 + 10))
}

const createRestoredIcon = (count) => {
    const size = getDynamicSize(count)
    return L.divIcon({
        html: `
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: 700;
                width: ${size}px;
                height: ${size}px;
                background: rgba(16, 185, 129, 0.85);
                border: 1.5px solid #047857;
                color: #ffffff;
                border-radius: 50%;
                box-shadow: 0 2px 4px rgba(0,0,0,0.15);
            ">
                ${count}
            </div>
        `,
        className: 'custom-marker-restored',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    })
}

const createToRestoreIcon = (count) => {
    const size = getDynamicSize(count)
    return L.divIcon({
        html: `
            <div style="
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 11px;
                font-weight: 700;
                width: ${size}px;
                height: ${size}px;
                background: rgba(239, 68, 68, 0.85);
                border: 1.5px solid #b91c1c;
                color: #ffffff;
                border-radius: 50%;
                box-shadow: 0 2px 4px rgba(0,0,0,0.15);
            ">
                ${count}
            </div>
        `,
        className: 'custom-marker-torestore',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2]
    })
}

// 🎯 Coordonnées centrées sur Madagascar
const MADAGASCAR_CENTER = [-18.7697, 46.8691]
const MADAGASCAR_BOUNDS = [
    [-25.6, 43.1], // Sud-Ouest
    [-11.8, 50.8] // Nord-Est
]

// Assure le redimensionnement correct sans planter au chargement
function MapResizeHandler() {
    const map = useMap()
    useEffect(() => {
        const timer = setTimeout(() => {
            map.invalidateSize()
        }, 100)
        return () => clearTimeout(timer)
    }, [map])
    return null
}

export default function MadagascarDashboard() {
    const [selectedRegion, setSelectedRegion] = useState('all')
    const [regionMetrics, setRegionMetrics] = useState([])
    const [globalMetrics, setGlobalMetrics] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const loadRegions = async () => {
            try {
                const res = await CarteService.getMapMetricsByRegion()
                setRegionMetrics(res?.data?.data || [])
            } catch (error) {
                console.error('Erreur régions', error)
            }
        }
        loadRegions()
    }, [])

    useEffect(() => {
        const loadMetrics = async () => {
            setLoading(true)
            try {
                const params = selectedRegion !== 'all' ? { region: selectedRegion } : {}
                const res = await CarteService.getMapMetrics(params)
                setGlobalMetrics(res?.data?.data)
            } catch (error) {
                console.error('Erreur metrics', error)
            } finally {
                setLoading(false)
            }
        }
        loadMetrics()
    }, [selectedRegion])

    const activeRegions =
        selectedRegion === 'all'
            ? regionMetrics
            : regionMetrics.filter((r) => String(r.regionId) === String(selectedRegion))

    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: '90%',
                borderRadius: '12px',
                boxSizing: 'border-box',
                fontFamily: '"Inter", "Segoe UI", sans-serif'
            }}
        >
            {/* 🗺️ Carte Leaflet */}
            <MapContainer
                center={MADAGASCAR_CENTER}
                zoom={5.8}
                minZoom={5.5}
                maxBounds={MADAGASCAR_BOUNDS}
                maxBoundsViscosity={1.0}
                zoomControl={false}
                style={{ height: '100%', width: '100%', zIndex: 1 }}
            >
                <MapResizeHandler />

                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                />

                {activeRegions.map((r) => {
                    const coord = getRegionCoords(r.regionName)
                    if (!coord) return null

                    return (
                        <React.Fragment key={r.regionId}>
                            {r.polygonesRestaures > 0 && (
                                <Marker
                                    position={[coord[0], coord[1] - 0.22]}
                                    icon={createRestoredIcon(r.polygonesRestaures)}
                                >
                                    <Popup>
                                        <b>🟢 {r.regionName}</b>
                                        <br />
                                        Polygones restaurés : <strong>{r.polygonesRestaures}</strong>
                                    </Popup>
                                </Marker>
                            )}

                            <Marker position={coord} icon={createPlantIcon(r.platesBandesActives)}>
                                <Popup>
                                    <div style={{ fontFamily: 'sans-serif', padding: '2px' }}>
                                        <b style={{ fontSize: '14px', color: '#1b4332' }}>🌱 {r.regionName}</b>
                                        <hr style={{ margin: '8px 0', border: '0', borderTop: '1px solid #e2e8f0' }} />
                                        Plates bandes actives :{' '}
                                        <strong style={{ color: '#2e7d32' }}>{r.platesBandesActives}</strong>
                                    </div>
                                </Popup>
                            </Marker>

                            {r.polygonesARestaurer > 0 && (
                                <Marker
                                    position={[coord[0], coord[1] + 0.22]}
                                    icon={createToRestoreIcon(r.polygonesARestaurer)}
                                >
                                    <Popup>
                                        <b>🔴 {r.regionName}</b>
                                        <br />
                                        Polygones à restaurer : <strong>{r.polygonesARestaurer}</strong>
                                    </Popup>
                                </Marker>
                            )}
                        </React.Fragment>
                    )
                })}
            </MapContainer>

            {/* 🔍 Overlay Filtre Région */}
            <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 1000 }}>
                <select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    style={{
                        width: '210px',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#ffffff',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#334155',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        outline: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <option value="all">🌍 Toutes les régions</option>
                    {regionMetrics.map((r) => (
                        <option key={r.regionId} value={r.regionId}>
                            {r.regionName}
                        </option>
                    ))}
                </select>
            </div>

            {/* 📊 Overlay Cartes KPI */}
            <div
                style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    zIndex: 1000,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 145px)',
                    gap: '8px'
                }}
            >
                <MetricCard label="Clients cumulatifs" value={globalMetrics?.customersCumulative} loading={loading} />
                <MetricCard
                    label="Plates-bandes"
                    icon="🌱"
                    value={globalMetrics?.platesBandesActives}
                    loading={loading}
                />
                <MetricCard label="Polygones totaux" value={globalMetrics?.polygonesTotal} loading={loading} />
                <MetricCard
                    label="Statut polygones"
                    value={
                        globalMetrics
                            ? { rest: globalMetrics.polygonesRestaures, aRest: globalMetrics.polygonesARestaurer }
                            : null
                    }
                    isSplit={true}
                    loading={loading}
                />
            </div>

            {/* ℹ️ Overlay Légende (Positionnée à 16px du bas du bloc) */}
            <div
                style={{
                    position: 'absolute',
                    bottom: '30px',
                    right: '30px',
                    zIndex: 1000,
                    backgroundColor: '#ffffff',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: '#334155',
                    border: '1px solid #cbd5e1'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            border: '2px solid #2e7d32',
                            backgroundColor: '#fff'
                        }}
                    />
                    <span>Nombre de plates-bandes actives</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: '#10b981'
                        }}
                    />
                    <span>Polygones restaurés</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div
                        style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            backgroundColor: '#ef4444'
                        }}
                    />
                    <span>Polygones à restaurer</span>
                </div>
            </div>
        </div>
    )
}

function MetricCard({ label, value, loading, icon, isSplit = false }) {
    return (
        <div
            style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '8px 10px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                justify: 'space-between',
                height: '65px'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#64748b',
                    fontSize: '10px',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                }}
            >
                {icon && <span>{icon}</span>}
                <span>{label}</span>
            </div>

            <div>
                {loading || value === undefined || value === null ? (
                    <span style={{ color: '#94a3b8', fontSize: '14px', fontWeight: '600' }}>...</span>
                ) : isSplit ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '13px',
                            fontWeight: '700'
                        }}
                    >
                        <span style={{ color: '#10b981' }}>🟢 {value.rest}</span>
                        <span style={{ color: '#cbd5e1', fontWeight: '300' }}>/</span>
                        <span style={{ color: '#ef4444' }}>🔴 {value.aRest}</span>
                    </div>
                ) : (
                    <span style={{ color: '#0f172a', fontSize: '18px', fontWeight: '700' }}>
                        {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
                    </span>
                )}
            </div>
        </div>
    )
}
